import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, User, CreditCard, Smartphone, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/crypto-data";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  created_at: string;
}

interface Transaction {
  id: string;
  crypto_symbol: string;
  crypto_name: string;
  amount: number;
  total_usd: number;
  status: string;
  created_at: string;
}

interface Device {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  is_blocked: boolean;
  last_login: string;
}

interface SupportRequest {
  id: string;
  status: string;
  created_at: string;
}

interface UserData extends Profile {
  transactions: Transaction[];
  devices: Device[];
  supportRequests: SupportRequest[];
  totalSpent: number;
  transactionCount: number;
}

const AdminUsers = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        return;
      }

      // Fetch all transactions
      const { data: transactions, error: transactionsError } = await supabase
        .from("transactions")
        .select("*");

      if (transactionsError) {
        console.error("Error fetching transactions:", transactionsError);
      }

      // Fetch all devices
      const { data: devices, error: devicesError } = await supabase
        .from("user_devices")
        .select("*");

      if (devicesError) {
        console.error("Error fetching devices:", devicesError);
      }

      // Fetch all support requests
      const { data: supportRequests, error: supportError } = await supabase
        .from("support_requests")
        .select("*");

      if (supportError) {
        console.error("Error fetching support requests:", supportError);
      }

      // Map data to users
      const usersData: UserData[] = (profiles || []).map((profile) => {
        const userTransactions = (transactions || []).filter(
          (t) => t.user_id === profile.user_id
        );
        const userDevices = (devices || []).filter(
          (d) => d.user_id === profile.user_id
        );
        const userSupportRequests = (supportRequests || []).filter(
          (s) => s.user_id === profile.user_id
        );

        const totalSpent = userTransactions
          .filter((t) => t.status === "completed")
          .reduce((sum, t) => sum + Number(t.total_usd), 0);

        return {
          ...profile,
          transactions: userTransactions,
          devices: userDevices,
          supportRequests: userSupportRequests,
          totalSpent,
          transactionCount: userTransactions.length,
        };
      });

      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ar-SA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ar-SA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-crypto-green/20 text-crypto-green">مكتمل</Badge>;
      case "pending":
        return <Badge className="bg-primary/20 text-primary">قيد الانتظار</Badge>;
      case "rejected":
        return <Badge className="bg-destructive/20 text-destructive">مرفوض</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const parseUserAgent = (ua: string | null) => {
    if (!ua) return "غير معروف";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    return "متصفح آخر";
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="glass border-border">
      <CardHeader>
        <CardTitle className="text-foreground flex items-center gap-2">
          <User className="w-5 h-5" />
          المستخدمون ({users.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {users.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            لا يوجد مستخدمون مسجلون حتى الآن
          </div>
        ) : (
          <div className="space-y-4">
            {users.map((user) => (
              <Collapsible
                key={user.id}
                open={expandedUser === user.id}
                onOpenChange={() =>
                  setExpandedUser(expandedUser === user.id ? null : user.id)
                }
              >
                <div className="border border-border rounded-lg overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary/50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {user.full_name || "بدون اسم"}
                          </p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">المعاملات</p>
                          <p className="font-bold text-foreground">{user.transactionCount}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">إجمالي الإنفاق</p>
                          <p className="font-bold text-primary">{formatCurrency(user.totalSpent)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">الأجهزة</p>
                          <p className="font-bold text-foreground">{user.devices.length}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">تاريخ التسجيل</p>
                          <p className="text-sm text-foreground">{formatDate(user.created_at)}</p>
                        </div>
                        <Button variant="ghost" size="sm">
                          {expandedUser === user.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t border-border p-4 bg-secondary/30 space-y-6">
                      {/* Transactions */}
                      <div>
                        <h4 className="font-medium text-foreground flex items-center gap-2 mb-3">
                          <CreditCard className="w-4 h-4" />
                          المعاملات ({user.transactions.length})
                        </h4>
                        {user.transactions.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="border-border">
                                  <TableHead className="text-right text-muted-foreground">التاريخ</TableHead>
                                  <TableHead className="text-right text-muted-foreground">العملة</TableHead>
                                  <TableHead className="text-right text-muted-foreground">الكمية</TableHead>
                                  <TableHead className="text-right text-muted-foreground">المبلغ</TableHead>
                                  <TableHead className="text-right text-muted-foreground">الحالة</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {user.transactions.map((tx) => (
                                  <TableRow key={tx.id} className="border-border">
                                    <TableCell className="text-foreground text-sm">
                                      {formatDateTime(tx.created_at)}
                                    </TableCell>
                                    <TableCell className="text-foreground">
                                      {tx.crypto_name} ({tx.crypto_symbol})
                                    </TableCell>
                                    <TableCell className="text-foreground font-mono">
                                      {tx.amount.toFixed(6)}
                                    </TableCell>
                                    <TableCell className="text-primary font-bold">
                                      {formatCurrency(tx.total_usd)}
                                    </TableCell>
                                    <TableCell>{getStatusBadge(tx.status)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">لا توجد معاملات</p>
                        )}
                      </div>

                      {/* Devices */}
                      <div>
                        <h4 className="font-medium text-foreground flex items-center gap-2 mb-3">
                          <Smartphone className="w-4 h-4" />
                          الأجهزة ({user.devices.length})
                        </h4>
                        {user.devices.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="border-border">
                                  <TableHead className="text-right text-muted-foreground">آخر دخول</TableHead>
                                  <TableHead className="text-right text-muted-foreground">IP</TableHead>
                                  <TableHead className="text-right text-muted-foreground">المتصفح</TableHead>
                                  <TableHead className="text-right text-muted-foreground">الحالة</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {user.devices.map((device) => (
                                  <TableRow key={device.id} className="border-border">
                                    <TableCell className="text-foreground text-sm">
                                      {formatDateTime(device.last_login)}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground font-mono text-sm">
                                      {device.ip_address || "-"}
                                    </TableCell>
                                    <TableCell className="text-foreground">
                                      {parseUserAgent(device.user_agent)}
                                    </TableCell>
                                    <TableCell>
                                      {device.is_blocked ? (
                                        <Badge className="bg-destructive/20 text-destructive">محظور</Badge>
                                      ) : (
                                        <Badge className="bg-crypto-green/20 text-crypto-green">نشط</Badge>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">لا توجد أجهزة مسجلة</p>
                        )}
                      </div>

                      {/* Support Requests */}
                      {user.supportRequests.length > 0 && (
                        <div>
                          <h4 className="font-medium text-foreground flex items-center gap-2 mb-3">
                            <MessageSquare className="w-4 h-4" />
                            طلبات الدعم ({user.supportRequests.length})
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {user.supportRequests.map((sr) => (
                              <Badge
                                key={sr.id}
                                variant="outline"
                                className={
                                  sr.status === "pending"
                                    ? "border-yellow-500 text-yellow-500"
                                    : sr.status === "closed"
                                    ? "border-muted-foreground text-muted-foreground"
                                    : "border-primary text-primary"
                                }
                              >
                                {sr.status === "pending"
                                  ? "قيد الانتظار"
                                  : sr.status === "closed"
                                  ? "مغلق"
                                  : sr.status}{" "}
                                - {formatDate(sr.created_at)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminUsers;